import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'

// ── Mode rules ──────────────────────────────────────────────────────────────
// `fullSize`: full-viewport overlay. Singleton — only ONE renders at a time
//   across the whole app, no matter how many components fire setLoading. This
//   prevents two stacked overlays when a page-level loader and a child loader
//   fire simultaneously.
// `cover`: parent-scoped overlay. Renders absolutely inside the nearest
//   positioned ancestor (e.g. a Dialog Paper, panel Box). The rest of the page
//   stays clear. Cover loaders do NOT participate in the singleton — when a
//   panel opens, only that panel should show loading, regardless of what else
//   in the app is loading.
// default (no prop): small inline indicator (for in-flow placements).

// Global singleton state for fullSize only.
let activeFullSizeIds = []
const listeners = new Set()
const notifyAll = () => listeners.forEach(fn => fn())

const hasSidebarPath = (pathname = '') =>
  /^\/(crm|configuration|admin|provider|user)(\/|$)/.test(pathname)

// User & admin layouts use a 300px sidebar; crm/configuration/provider use 250px.
// The overlay's left offset must match so it never covers the sidebar.
const getSidebarOffset = (pathname = '') =>
  /^\/(user|admin)(\/|$)/.test(pathname) ? '300px' : '250px'

const buildFullSizeSx = (sidebarOffset) => ({
  position: 'fixed',
  top: 0,
  bottom: 0,
  right: 0,
  left: sidebarOffset ? { xs: 0, md: sidebarOffset } : 0,
  zIndex: 1500,
  backdropFilter: 'blur(1.5px)',
  background: 'rgba(255,255,255,0.35)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: 'auto',
  animation: 'wlOverlayIn 0.3s ease',
  '@keyframes wlOverlayIn': {
    '0%':   { opacity: 0 },
    '100%': { opacity: 1 },
  },
})

// Cover overlay: blur the panel content. The loader sits in a sticky wrapper
// (see CoverOverlay) so it stays in the visible area of tall panels instead of
// being centered far below the fold.
const coverSx = {
  position: 'absolute',
  inset: 0,
  zIndex: 1400,
  backdropFilter: 'blur(1.5px)',
  background: 'rgba(255,255,255,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  pt: '60px',
  pointerEvents: 'auto',
  animation: 'wlOverlayIn 0.3s ease',
  '@keyframes wlOverlayIn': {
    '0%':   { opacity: 0, transform: 'scale(0.96)' },
    '100%': { opacity: 1, transform: 'scale(1)' },
  },
}

const inlineSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  minHeight: '50vh',
  py: 2,
}

const loaderSx = (size) => ({
  transform: 'rotateZ(45deg)',
  perspective: '1000px',
  borderRadius: '50%',
  width: `${size}px`,
  height: `${size}px`,
  color: '#A72C68',
  position: 'relative',
  filter: 'drop-shadow(0 4px 14px rgba(167,44,104,0.45))',
  '@keyframes wlLoaderSpin': {
    '0%, 100%': { boxShadow: '.5em 0px 0 0px currentcolor' },
    '12%':      { boxShadow: '.5em .5em 0 0 currentcolor' },
    '25%':      { boxShadow: '0 .5em 0 0px currentcolor' },
    '37%':      { boxShadow: '-.5em .5em 0 0 currentcolor' },
    '50%':      { boxShadow: '-.5em 0 0 0 currentcolor' },
    '62%':      { boxShadow: '-.5em -.5em 0 0 currentcolor' },
    '75%':      { boxShadow: '0px -.5em 0 0 currentcolor' },
    '87%':      { boxShadow: '.5em -.5em 0 0 currentcolor' },
  },
  '&::before, &::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
    width: 'inherit',
    height: 'inherit',
    borderRadius: '50%',
    transform: 'rotateX(70deg)',
    animation: 'wlLoaderSpin 1s linear infinite',
  },
  '&::after': {
    color: '#E5A8C2',
    transform: 'rotateY(70deg)',
    animationDelay: '.4s',
  },
})

const Waveloading = ({ fullSize, cover, size, compact }) => {
  // ── COMPACT mode: bare loader, no wrapper/overlay/min-height ──
  // For in-flow spots with tight space (e.g. inside a button). Renders just the
  // branded spinner at the given size so it sits centered by its parent.
  if (compact) {
    return <Box sx={loaderSx(size || 22)} />
  }

  // ── COVER mode: parent-scoped overlay, no singleton dedup ──
  // When a panel/dialog opens and shows its loader, the rest of the screen
  // stays clear — only the panel is blurred. Multiple cover loaders can
  // coexist in different parents without interfering.
  if (cover) {
    return <CoverOverlay size={size || 80} />
  }

  // ── INLINE mode (no prop): small in-flow indicator, no singleton dedup ──
  if (!fullSize) {
    const loaderSize = size || 48
    return (
      <Box sx={inlineSx}>
        <Box sx={loaderSx(loaderSize)} />
      </Box>
    )
  }

  // ── FULLSIZE mode: viewport overlay, singleton (only one renders at a time) ──
  // The singleton hook logic lives in FullSizeSingletonInstance below, so the
  // parent Waveloading component itself calls no hooks — early returns above
  // are safe.
  return <FullSizeSingletonInstance size={size || 120} />
}

// Cover overlays are `position: absolute`, so they anchor to the nearest
// *positioned* ancestor — not necessarily the wrapper they were written into.
// When that wrapper is still `position: static` (the common case, since call
// sites rarely add `position: relative`), the overlay stretches over some far-up
// scroll container instead and centers the spinner hundreds of pixels below the
// fold, so the loader looks like it never appeared. Promote the immediate parent
// to `relative` on mount and put it back on unmount, so every existing
// `<Waveloading cover/>` anchors where its author intended.
const CoverOverlay = ({ size }) => {
  const ref = useRef(null)

  useEffect(() => {
    const parent = ref.current?.parentElement
    if (!parent) return
    const previousPosition = parent.style.position
    const previousMinHeight = parent.style.minHeight
    if (window.getComputedStyle(parent).position === 'static') parent.style.position = 'relative'
    // An empty/short parent would collapse the overlay; keep room for the loader.
    if (parent.getBoundingClientRect().height < 320) parent.style.minHeight = '320px'
    return () => {
      parent.style.position = previousPosition
      parent.style.minHeight = previousMinHeight
    }
  }, [])

  return (
    <Box ref={ref} sx={coverSx}>
      <Box sx={{ position: 'sticky', top: '40vh', p: '24px' }}>
        <Box sx={loaderSx(size)} />
      </Box>
    </Box>
  )
}

// Separate component so the singleton hook logic only runs in fullSize mode.
const FullSizeSingletonInstance = ({ size }) => {
  const idRef = useRef(null)
  const [, force] = useState(0)

  if (idRef.current === null) {
    idRef.current = `wl-${Math.random().toString(36).slice(2)}-${Date.now()}`
  }

  useEffect(() => {
    const id = idRef.current
    activeFullSizeIds.push(id)
    const myListener = () => force(x => x + 1)
    listeners.add(myListener)
    notifyAll()
    return () => {
      activeFullSizeIds = activeFullSizeIds.filter(x => x !== id)
      listeners.delete(myListener)
      notifyAll()
    }
  }, [])

  const isOwner = activeFullSizeIds[0] === idRef.current
  if (!isOwner) return null

  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const sidebarOffset = hasSidebarPath(pathname) ? getSidebarOffset(pathname) : 0

  return (
    <Box sx={buildFullSizeSx(sidebarOffset)}>
      <Box sx={loaderSx(size)} />
    </Box>
  )
}

export default Waveloading
